import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
} from '@mui/material';
import {
  FitnessCenter,
  AccessTime,
  People,
  LocalAtm,
  Notifications,
  CalendarMonth,
  PhoneAndroid,
  School,
} from '@mui/icons-material';

const features = [
  {
    icon: <FitnessCenter sx={{ fontSize: 40 }} />,
    title: 'List Your Gym',
    description: 'Share your gym space with others and earn money',
  },
  {
    icon: <AccessTime sx={{ fontSize: 40 }} />,
    title: 'Hourly Bookings',
    description: 'Flexible booking system with hourly rates',
  },
  {
    icon: <People sx={{ fontSize: 40 }} />,
    title: 'Group Access',
    description: 'Control how many people can use your space',
  },
  {
    icon: <LocalAtm sx={{ fontSize: 40 }} />,
    title: 'Set Your Price',
    description: 'You decide how much to charge per hour',
  },
  {
    icon: <Notifications sx={{ fontSize: 40 }} />,
    title: 'Smart Notifications',
    description: 'Get reminders for your upcoming bookings',
  },
  {
    icon: <CalendarMonth sx={{ fontSize: 40 }} />,
    title: 'Calendar Integration',
    description: 'Sync your bookings with your calendar',
  },
  {
    icon: <School sx={{ fontSize: 40 }} />,
    title: 'Personal Training & Coaching',
    description: 'Offer personal training and coaching services to maximize your revenue',
  },
];

const Home = () => {
  const navigate = useNavigate();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallButton, setShowInstallButton] = useState(false);

  useEffect(() => {
    // Check if app is already installed
    const isInstalled = 
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true ||
      localStorage.getItem('appInstalled') === 'true';

    if (isInstalled) {
      setShowInstallButton(false);
      return;
    }

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallButton(true);
    };

    // Listen for the app installed event
    const handleAppInstalled = () => {
      setShowInstallButton(false);
      setDeferredPrompt(null);
      localStorage.setItem('appInstalled', 'true');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleDownloadApp = async () => {
    if (deferredPrompt) {
      // Show the install prompt
      deferredPrompt.prompt();

      // Wait for the user to respond
      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === 'accepted') {
        console.log('User accepted the install prompt');
        setShowInstallButton(false);
        setDeferredPrompt(null);
        localStorage.setItem('appInstalled', 'true');
      } else {
        console.log('User dismissed the install prompt');
      }
    } else {
      // Fallback: Show manual installation instructions
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const isAndroid = /Android/.test(navigator.userAgent);

      if (isIOS) {
        alert(
          'To install GymRat on your iOS device:\n\n' +
          '1. Tap the Share button (square with arrow) at the bottom\n' +
          '2. Scroll down and select "Add to Home Screen"\n' +
          '3. Tap "Add"'
        );
      } else if (isAndroid) {
        alert(
          'To install GymRat on your Android device:\n\n' +
          '1. Tap the menu (3 dots) in your browser\n' +
          '2. Select "Add to Home screen" or "Install app"\n' +
          '3. Tap "Add" or "Install"'
        );
      } else {
        alert(
          'To install GymRat:\n\n' +
          '1. Look for the install icon in your browser\'s address bar\n' +
          '2. Or use your browser\'s menu to find "Install" or "Add to Home Screen"'
        );
      }
    }
  };

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          py: 8,
          mb: 6,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center" justifyContent="center">
            <Grid item xs={12} md={8}>
              <Typography variant="h2" component="h1" gutterBottom>
                Share Your Gym Space
              </Typography>
              <Typography variant="h5" paragraph>
                List your gym, set your price, and start earning. Offer personal training and coaching services to maximize revenue. Or find and book the perfect gym space for your workout.
              </Typography>
              <Box sx={{ mt: 4 }}>
                <Button
                  variant="contained"
                  color="secondary"
                  size="large"
                  onClick={() => navigate('/gyms')}
                  sx={{ mr: 2, mb: 2 }}
                >
                  Find a Gym
                </Button>
                <Button
                  variant="outlined"
                  color="inherit"
                  size="large"
                  onClick={() => navigate('/list-gym')}
                  sx={{ mr: 2, mb: 2 }}
                >
                  List Your Gym
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  size="large"
                  startIcon={<PhoneAndroid />}
                  onClick={handleDownloadApp}
                  sx={{ mb: 2 }}
                >
                  Install Mobile App
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ mb: 8 }}>
        <Typography variant="h3" component="h2" align="center" gutterBottom>
          Why Choose GymRat?
        </Typography>
        <Grid container spacing={4} sx={{ mt: 2 }}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                  },
                }}
              >
                <CardContent sx={{ flexGrow: 1, textAlign: 'center' }}>
                  <Box sx={{ color: 'primary.main', mb: 2 }}>
                    {feature.icon}
                  </Box>
                  <Typography variant="h5" component="h3" gutterBottom>
                    {feature.title}
                  </Typography>
                  <Typography color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* CTA Section */}
      <Box
        sx={{
          bgcolor: 'secondary.main',
          color: 'white',
          py: 8,
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h3" component="h2" align="center" gutterBottom>
            Ready to Get Started?
          </Typography>
          <Typography variant="h6" align="center" paragraph>
            Join our community of gym owners and fitness enthusiasts today. List your gym, offer personal training and coaching services, and start earning from unused hours.
          </Typography>
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={() => navigate('/gyms')}
            >
              Browse Available Gyms
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Home; 